import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ArrowLeft, Loader2, Send, Sparkles, Headset, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { VoiceInput } from '../../components/VoiceInput';

/**
 * Evie — client Messages assistant. Chats with the client (Emergent LLM /
 * Evie brain) and can escalate the thread to the iEvntür admin team, which
 * posts into the canonical admin↔client support thread + notifies admins.
 */
const SUGGESTIONS = [
  'When is my next event?',
  'How do I download an event recap?',
  'How do I invite my team?',
  'I need to speak with someone',
];

const EvieAssistant = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi, I'm Evie — your iEvntür assistant. Ask me about your events, bookings, recaps or billing. I can also connect you with our team anytime." },
  ]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [escalateSuggested, setEscalateSuggested] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const sessionRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, escalateSuggested]);

  const send = useCallback(async (text) => {
    const content = (text ?? draft).trim();
    if (!content || sending) return;
    setDraft('');
    setEscalateSuggested(false);
    const nextHistory = [...messages, { role: 'user', content }];
    setMessages(nextHistory);
    setSending(true);
    try {
      const r = await api.post('/client/evie/chat', {
        message: content,
        session_id: sessionRef.current,
        history: nextHistory.slice(-10).map((m) => ({ role: m.role, content: m.content })),
      });
      sessionRef.current = r.data.session_id;
      setMessages((prev) => [...prev, { role: 'assistant', content: r.data.reply }]);
      setEscalateSuggested(Boolean(r.data.escalate_suggested));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Evie is unavailable right now');
      setMessages((prev) => [...prev, { role: 'assistant', content: "Sorry — I hit a snag. You can connect with our team below or call 404-482-0018." }]);
      setEscalateSuggested(true);
    } finally {
      setSending(false);
    }
  }, [draft, messages, sending]);

  const escalate = async () => {
    setEscalating(true);
    try {
      const r = await api.post('/client/evie/escalate', {
        session_id: sessionRef.current,
        reason: 'Client requested help via Evie assistant',
        transcript: messages.filter((m) => m.content).slice(-10).map((m) => ({ role: m.role, content: m.content })),
      });
      setEscalated(true);
      setEscalateSuggested(false);
      setMessages((prev) => [...prev, { role: 'assistant', content: "I've connected you with the iEvntür team — they'll follow up in your Support conversation shortly. You can continue there anytime.", system: true }]);
      toast.success('Connected to the iEvntür team');
      setTimeout(() => navigate(`/client/messages/support`), 1400);
      return r;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not escalate — please try again');
    } finally {
      setEscalating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="client-evie-assistant-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/messages')} data-testid="evie-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 leading-tight">Ask Evie</h1>
              <p className="text-xs text-gray-500">Your iEvntür assistant</p>
            </div>
          </div>
          <a href="tel:+14044820018" className="ml-auto" data-testid="evie-call-btn">
            <Button variant="outline" size="sm" className="border-violet-300 text-violet-700 hover:bg-violet-50">
              <Phone className="w-4 h-4 mr-1.5" /> 404-482-0018
            </Button>
          </a>
        </div>
      </header>

      <div className="flex-1 max-w-3xl w-full mx-auto px-4 py-6 overflow-y-auto" data-testid="evie-messages">
        <div className="space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
                m.role === 'user' ? 'bg-violet-600 text-white'
                : m.system ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-white border border-gray-200 text-gray-800'}`}>
                {m.role === 'assistant' && !m.system && (
                  <div className="text-[10px] font-semibold text-violet-600 mb-0.5 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Evie
                  </div>
                )}
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-2xl px-3.5 py-2.5">
                <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
              </div>
            </div>
          )}

          {escalateSuggested && !escalated && (
            <div className="flex justify-start" data-testid="evie-escalate-prompt">
              <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-violet-50 border border-violet-200">
                <p className="text-sm text-violet-900 flex items-center gap-1.5 font-medium">
                  <Headset className="w-4 h-4" /> Want me to connect you with the iEvntür team?
                </p>
                <Button size="sm" onClick={escalate} disabled={escalating}
                  className="mt-2 bg-violet-600 hover:bg-violet-700 text-white" data-testid="evie-escalate-btn">
                  {escalating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Headset className="w-4 h-4 mr-1.5" />}
                  Connect me with a person
                </Button>
              </div>
            </div>
          )}

          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid="evie-suggestions">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)}
                  className="text-xs bg-white border border-gray-200 hover:border-violet-300 hover:text-violet-700 rounded-full px-3 py-1.5 text-gray-600 transition"
                  data-testid={`evie-suggestion-${s.slice(0, 10).replace(/\s+/g, '-').toLowerCase()}`}>
                  {s}
                </button>
              ))}
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      <div className="bg-white border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex gap-2">
          <VoiceInput onTranscript={(t) => setDraft(t)} disabled={sending} />
          <Input value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
            placeholder="Ask Evie anything…" data-testid="evie-input" disabled={sending} />
          <Button onClick={() => send()} disabled={sending || !draft.trim()}
            className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="evie-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EvieAssistant;
